import { useState } from 'react';
import { createPortal } from 'react-dom';

import { Button, Icon } from '@sb/design-system';
import { usePortalContainer } from '@sb/design-system/utility';
import type { RestartServiceName } from '@sb/remote-control/types';
import type { RoutineRunnerState } from '@sb/routine-runner';
import { useToast, useRobotStateKind } from '@sbrc/hooks';
import { executeHostCommand } from '@sbrc/services';

/**
 * Robot state kinds that imply active control/motion, each mapped to the
 * instruction shown when the restart is gated. Restarting arm-control-bot /
 * botman while the arm is active is worse than a stale flag, so the action is
 * gated (the banner stays visible) while in any of these states.
 */
const MOTION_STATE_REASONS: Partial<
  Record<RoutineRunnerState['kind'], string>
> = {
  RoutineRunning: 'Stop the running routine before restarting services.',
  RunningAdHocCommand:
    'Wait for the current movement to finish before restarting services.',
  Antigravity: 'Exit antigravity before restarting services.',
  AntigravitySlow: 'Exit antigravity before restarting services.',
};

const DEFAULT_TITLE =
  'Recent feature flag changes require a service restart to take effect.';

interface ServiceRestartBannerProps {
  /** Called once services have been told to restart, to clear pending state. */
  onRestarted?: () => void;
  /** Customer-facing settings pass wording about their setting, not about flags. */
  title?: string;
  /** Which service to bounce; only botman when it alone lags a flag. */
  service?: RestartServiceName;
}

export function ServiceRestartBanner({
  onRestarted,
  title = DEFAULT_TITLE,
  service = 'all',
}: ServiceRestartBannerProps) {
  const { setToast } = useToast();
  // Inside a dialog this is the dialog itself, which is above the top layer.
  const portalContainer = usePortalContainer();
  const stateKind = useRobotStateKind({});
  const [isRestarting, setIsRestarting] = useState(false);

  const motionReason = stateKind ? MOTION_STATE_REASONS[stateKind] : undefined;
  const isMotionActive = Boolean(motionReason);

  const handleRestart = async () => {
    setIsRestarting(true);

    try {
      const succeeded = await executeHostCommand({
        command: 'services_restart',
        service,
      });

      if (succeeded) {
        setToast({
          kind: 'progress',
          message: 'Restarting services…',
        });

        onRestarted?.();
      } else {
        setToast({
          kind: 'error',
          message: 'Failed to restart services.',
        });
      }
    } finally {
      setIsRestarting(false);
    }
  };

  // Full-width bar fixed to the top of the viewport (via a portal), on a
  // distinct amber surface so it stands out from the white flag column and
  // stays visible even when the user has scrolled deep into the list.
  return createPortal(
    <div
      role="alert"
      data-testid="feature-flags-restart-banner"
      className="tw-fixed tw-top-0 tw-inset-x-0 tw-z-40 tw-flex tw-flex-row tw-items-start tw-gap-16 tw-surface-mango tw-rounded-none tw-px-24 tw-py-12 tw-shadow-30"
    >
      <Icon kind="exclamationTriangle" className="tw-mt-2 tw-shrink-0" />

      <div className="tw-flex tw-flex-1 tw-flex-col tw-gap-2">
        <span className="tw-font-medium">{title}</span>
        <span className="tw-text-13">
          Restarting will briefly disconnect this UI while services come back
          up.
        </span>
        {motionReason && (
          <span
            className="tw-text-13 tw-font-medium"
            data-testid="feature-flags-restart-banner-disabled-reason"
          >
            {motionReason}
          </span>
        )}
      </div>

      <Button
        data-testid="feature-flags-restart-banner-button"
        variant="Elevated"
        disabled={isMotionActive || isRestarting}
        onClick={handleRestart}
        className="tw-shrink-0"
      >
        Restart services
      </Button>
    </div>,
    portalContainer,
  );
}
